import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { NonDismissibleDialog } from '@scoir/modals'
import { endOfDay, isAfter, isBefore, isValid as isValidDate } from 'date-fns'
import { validate as isValidEmail } from 'email-validator'

import api from 'common/api'
import { useScid } from 'store/user/hooks'

import type { deadline, extension } from '../index'
import Deadline from './FormFields/Deadline'
import Email from './FormFields/Email'
import ExtensionDatePicker from './FormFields/ExtensionDatePicker'

const { Body, Footer } = NonDismissibleDialog

export const MODAL_LABEL = 'Cut-Off Extension'
export const FORM_TEXT = {
  studentEmail: 'Please enter a valid email address',
  deadlineId: 'Please select a deadline',
  extensionDate: 'Please select a date',
  duplicate: 'The student already has an extension for this application deadline.',
  notFound: 'No student found with that email address. Please try again.',
  deadline: 'Please select an active application deadline',
  emailHelper: "You must enter the student's email address as provided in their application.",
  emailLabel: `Enter applicant's email address`,
  deadlineLabel: 'Select Application Deadline',
  dateLabel: 'Extension cut-off date',
  dateHelper: 'Enter the date the extension will expire in MM/DD/YYYY format',
  dateMinError: 'The extension cut-off date must be later than the application deadline cut-off date.',
  dateMaxError: 'The extension cut-off date must be on or before July 31.',
  genericSubmitError:
    'There was an error submitting your request. Please try again or contact Support for additional assistance.',
  studentNotFoundError: 'No students found with that email address',
  noChange: 'Please make an edit.',
  noDeadlines: 'Please add a non-rolling application deadline before creating an extension',
}

// API RESPONSES
export const STUDENT_NOT_FOUND_ERROR_MESSAGE = 'invalid email - student not found'
export const EXTENSION_ALREADY_EXISTS_ERROR_MESSAGE =
  'failed to write app extension: an app deadline extension for that student/college/app deadline already exists'

export const defaultValues = {
  studentEmail: '',
  deadlineId: '',
  extensionDate: '',
}

const useStyles = makeStyles((theme) => ({
  form: {
    '& .MuiTextField-root': {
      width: '100%',
      marginBottom: `${theme.spacing(3)}px`,
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
  },
  error: {
    margin: `${theme.spacing(1)}px ${theme.spacing(3)}px ${theme.spacing(2)}px`,
  },
}))

const calcDates = (cutoff: string, appCycle: number) => {
  const cutOffDate = new Date(cutoff)
  const calcMinDate = isAfter(cutOffDate, new Date()) ? cutOffDate : new Date()
  const calcMaxDate = new Date()
  calcMaxDate.setFullYear(appCycle)
  calcMaxDate.setMonth(6, 31)

  return [calcMinDate, endOfDay(calcMaxDate)]
}
const getErrorMessage = (errorMessage: string) => {
  switch (errorMessage) {
    case STUDENT_NOT_FOUND_ERROR_MESSAGE:
      return FORM_TEXT.studentNotFoundError
    case EXTENSION_ALREADY_EXISTS_ERROR_MESSAGE:
      return FORM_TEXT.duplicate
    default:
      return FORM_TEXT.genericSubmitError
  }
}

type modalProps = {
  extensions: extension[]
  deadlines: deadline[]
  extensionInFocus?: extension
  onClose: (_?: object, reason?: string) => void
  getExtensions: () => void
}

export type formVals = {
  studentEmail: string
  deadlineId: string
  extensionDate: string | Date | number
}
export type formErrors = {
  studentEmail?: string
  deadlineId?: string
  extensionDate?: string
}

const ExtensionsModalContent = ({ deadlines, getExtensions, onClose, extensionInFocus, extensions }: modalProps) => {
  const [submitError, setSubmitError] = useState('')
  const [formValues, setFormValues] = useState<formVals>({
    studentEmail: extensionInFocus?.email ?? '',
    deadlineId: extensionInFocus?.deadlineId ?? '',
    extensionDate: extensionInFocus?.extensionDate ?? '',
  })
  const [formErrors, setFormErrors] = useState({})
  const isEditing = extensionInFocus !== undefined
  const scid = useScid()
  const classes = useStyles()

  useEffect(() => {
    if (deadlines.length <= 0) {
      setFormErrors((prevState) => ({
        ...prevState,
        deadlineId: FORM_TEXT.noDeadlines,
      }))
    }
  }, [deadlines])

  const [cutoff, appCycle] = useMemo(() => {
    if (formValues.deadlineId) {
      const deadline = deadlines.find((d) => d.Id === formValues.deadlineId)
      return [deadline?.CutoffDate, deadline?.ApplicationCycle]
    }
    return []
  }, [formValues.deadlineId, deadlines])

  const [minDate, maxDate] = useMemo(() => {
    if (cutoff && appCycle) {
      return calcDates(cutoff, appCycle)
    }
    const today = new Date()
    return [today, today]
  }, [cutoff, appCycle])

  const isDuplicateExt = useCallback(
    (email, id, date) => {
      let editDup,
        newExtDup = false
      if (email && id) {
        if (isEditing) {
          editDup = id === extensionInFocus.deadlineId && date === extensionInFocus.extensionDate
        } else {
          newExtDup = extensions.some((e) => e.email === email && e.deadlineId === id)
        }
      }
      return editDup || newExtDup ? true : false
    },
    [extensionInFocus?.deadlineId, extensionInFocus?.extensionDate, extensions, isEditing],
  )

  const handleInputsChange = (name: string, value: string | Date | number) => {
    setFormErrors({})
    const updatedFormValues = { ...formValues, [name]: value }
    setFormValues(updatedFormValues)

    switch (name) {
      case 'studentEmail': {
        if (typeof value === 'string') {
          if (!value || !isValidEmail(value)) {
            setFormErrors((prevState) => ({ ...prevState, studentEmail: FORM_TEXT.studentEmail }))
          }
          if (isDuplicateExt(value, formValues.deadlineId, formValues.extensionDate)) {
            setFormErrors((prevState) => ({
              ...prevState,
              deadlineId: FORM_TEXT.duplicate,
              studentEmail: FORM_TEXT.duplicate,
            }))
          }
          if (deadlines.length <= 0) {
            setFormErrors((prevState) => ({
              ...prevState,
              deadlineId: FORM_TEXT.noDeadlines,
            }))
          }
        }
        break
      }
      case 'deadlineId': {
        if (isDuplicateExt(formValues.studentEmail, String(value), formValues.extensionDate)) {
          setFormErrors((prevState) => ({
            ...prevState,
            deadlineId: FORM_TEXT.duplicate,
            studentEmail: FORM_TEXT.duplicate,
          }))
        }
        break
      }
      case 'extensionDate': {
        const date = new Date(value)
        if (isBefore(date, minDate)) setFormErrors((prevState) => ({ ...prevState, [name]: FORM_TEXT.dateMinError }))

        if (isAfter(date, maxDate)) setFormErrors((prevState) => ({ ...prevState, [name]: FORM_TEXT.dateMaxError }))
        break
      }
      default: {
        break
      }
    }
  }

  const onSaveError = (err: string) => {
    const errMsg = getErrorMessage(err)
    setSubmitError(errMsg)
  }
  const onSaveSuccess = () => {
    getExtensions()
    onClose()
  }

  const validateForm = () => {
    let valid = true
    if (!formValues.studentEmail || !isValidEmail(formValues.studentEmail)) {
      setFormErrors((prevState) => ({ ...prevState, studentEmail: FORM_TEXT.studentEmail }))
      valid = false
    }

    if (!formValues.deadlineId) {
      setFormErrors((prevState) => ({ ...prevState, deadlineId: FORM_TEXT.deadlineLabel }))
      valid = false
    }

    if (isDuplicateExt(formValues.studentEmail, formValues.deadlineId, formValues.extensionDate)) {
      setFormErrors((prevState) => ({
        ...prevState,
        deadlineId: FORM_TEXT.duplicate,
        studentEmail: FORM_TEXT.duplicate,
      }))
      valid = false
    }

    if (!formValues.extensionDate || !isValidDate(new Date(formValues.extensionDate))) {
      setFormErrors((prevState) => ({ ...prevState, extensionDate: FORM_TEXT.dateHelper }))
      valid = false
    } else {
      const date = new Date(formValues.extensionDate)
      if (isBefore(date, minDate)) {
        setFormErrors((prevState) => ({ ...prevState, extensionDate: FORM_TEXT.dateMinError }))
        valid = false
      }

      if (isAfter(date, maxDate)) {
        setFormErrors((prevState) => ({ ...prevState, extensionDate: FORM_TEXT.dateMaxError }))
        valid = false
      }
    }
    return valid
  }

  const submit = () => {
    setSubmitError('')
    let promise
    if (!validateForm()) return

    if (!isEditing) {
      promise = api.post(`/api/colleges/${scid}/applications/extensions`, formValues)
    } else {
      const id = extensionInFocus ? extensionInFocus['applicationExtensionId'] : ''
      promise = api.put(`/api/colleges/${scid}/applications/extensions/${id}`, formValues)
    }
    promise
      .then(onSaveSuccess)
      .catch((err) => Promise.reject(err.text()))
      .catch(onSaveError)
  }
  return (
    <>
      <Body>
        <form
          id="extension-form"
          autoComplete="on"
          className={classes.form}
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <Box mb={1}>
            <Typography color="textSecondary">{FORM_TEXT.emailHelper}</Typography>
          </Box>
          <Email
            formValues={formValues}
            formErrors={formErrors}
            isEditing={isEditing}
            handleInputsChange={handleInputsChange}
          />
          <Deadline
            formValues={formValues}
            formErrors={formErrors}
            deadlines={deadlines}
            handleInputsChange={handleInputsChange}
          />
          <ExtensionDatePicker
            formValues={formValues}
            handleInputsChange={handleInputsChange}
            maxDate={maxDate}
            minDate={minDate}
          />
        </form>
      </Body>
      <Footer>
        <Footer.CloseButton>Cancel</Footer.CloseButton>
        <Footer.PrimaryActionButton
          form="extension-form"
          type="submit"
        >
          Save
        </Footer.PrimaryActionButton>
      </Footer>
      {submitError && (
        <Box
          display="flex"
          className={classes.error}
        >
          <Box marginLeft="auto">
            <Typography
              color="error"
              role="alert"
            >
              {submitError}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  )
}

type extensionModalProps = {
  open: boolean
  onClose: (_?: object, reason?: string) => void
  deadlines: deadline[]
  getExtensions: () => void
  extensionInFocus?: extension
  extensions: extension[]
}

const ExtensionsModal = ({
  open,
  onClose,
  deadlines,
  getExtensions,
  extensionInFocus,
  extensions,
}: extensionModalProps) => {
  return (
    <NonDismissibleDialog
      open={open}
      onClose={onClose}
      scroll="paper"
      headerText={MODAL_LABEL}
    >
      <ExtensionsModalContent
        onClose={onClose}
        deadlines={deadlines}
        getExtensions={getExtensions}
        extensionInFocus={extensionInFocus}
        extensions={extensions}
      />
    </NonDismissibleDialog>
  )
}
export default ExtensionsModal
